import { useEffect } from 'react';
import { View } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { DetectedTripsPreview } from '@/components/trips/DetectedTripsPreview';
import { useUserStore } from '@/state/userStore';
import { useAuthUid } from '@/hooks/useAuthUid';
import type { InboxProviderId } from '@/services/inboxSync';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';
import { useTheme } from '@/theme';
import { LoadingState } from '@/components/ui/LoadingState';
import { useToast } from '@/components/ui/ToastProvider';
import { useTripDetectionSessionStore } from '@/state/tripDetectionSessionStore';
import { TripErrorBoundary } from '@/utils/errorBoundary';

function parseProvider(p: string | undefined): InboxProviderId {
  if (p === 'outlook') return 'outlook';
  if (p === 'icloud') return 'icloud';
  return 'gmail';
}

/**
 * Full-screen trip detection after inbox OAuth (Trips tab).
 * Presented as a root-stack fullScreenModal to avoid RN Modal + auth-session presenter races on iOS.
 */
export default function TripDetectionScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { showSuccess } = useToast();
  const clearSession = useTripDetectionSessionStore((s) => s.clearSession);
  const setSession = useTripDetectionSessionStore((s) => s.setSession);
  const params = useLocalSearchParams<{ provider?: string; taskId?: string }>();
  const raw = params.provider;
  const providerParam = Array.isArray(raw) ? raw[0] : raw;
  const rawTaskId = params.taskId;
  const taskId = Array.isArray(rawTaskId) ? rawTaskId[0] : rawTaskId;
  const provider = parseProvider(typeof providerParam === 'string' ? providerParam : undefined);
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const authUid = useAuthUid();
  const userId = firebaseUser?.uid ?? authUid ?? '';

  useEffect(() => {
    completeOAuthWebSession();
  }, []);

  if (!userId) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        <LoadingState variant="fullscreen" message="Finishing sign-in…" />
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <TripErrorBoundary name="TripDetection">
        <DetectedTripsPreview
          key={`${userId}-${provider}`}
          presentation="screen"
          visible
          initialTaskId={typeof taskId === 'string' ? taskId : undefined}
          userId={userId}
          provider={provider}
          onMinimize={(session) => {
            setSession(session);
            router.back();
          }}
          onClose={() => {
            clearSession();
            router.back();
          }}
          onTripsCreated={() => {
            clearSession();
            showSuccess('Trips imported successfully!');
          }}
        />
      </TripErrorBoundary>
    </View>
  );
}
